import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import { Compass3D } from '../decor/compass/Compass3D'
import { CompassLines } from '../decor/compass/VerticalShellCompassLines'
import { Ruler3D } from '../decor/Ruler3D'
import { TankDecorGroup3D } from '../decor/TankDecorGroup3D'
import { FeatureDispatch } from '../features/FeatureDispatch'
import { ShellCourseFeature3D } from '../features/verticalTankShell/ShellCourseFeature3D'
import {
  planeGeometry,
  xWrappedShellGeometry,
  yWrappedShellGeometry,
} from '@asset-viz/utils/geometryDefines'
import { extractScale, Vec3 } from '@asset-viz/utils/helpers'
import React from 'react'
import { Vector3 } from 'three'
import {
  GeckoAssetModel,
  GeckoCoatingRegion,
  GeckoHorizontalTankShell,
  GeckoHorizontalWeld,
  GeckoLinerRegion,
  GeckoMeasurement,
  GeckoPlate,
  GeckoPolygonalPlate,
  GeckoShellCourse,
  GeckoVerticalTankShell,
  GeckoVerticalWeld,
} from 'types'

import { ModelSurface3D } from './ModelSurface3D'
import { Surface3DGroupProps } from './Surface3D'

/**
 * A surface representing a tank shell, plotted on a cartesian plane, with the
 * center of its base at "position" and decorated with Rich Asset features
 * specific to shells.
 * @param model               The tank shell component model
 * @param assetModel          The asset model for the asset that the shell is a part of
 * @param groupProps          Position, rotation, scale, and other 3D properties
 * @constructor
 */
export const ShellSurface3D: React.FC<
  {
    model: GeckoVerticalTankShell | GeckoHorizontalTankShell
    assetModel: GeckoAssetModel
  } & Surface3DGroupProps
> = ({ model, assetModel, ...groupProps }) => {
  const { show3D } = useAssetVizInternal()

  const {
    bounds,
    scale,
    manways,
    rectangularManways,
    nozzles,
    verticalWelds,
    horizontalWelds,
    courses,
    plates,
    polygonalPlates,
    fixedSensors,
    isHorizontalShell,
    coatingRegions,
    linerRegions,
  } = React.useMemo(() => {
    const verticalWelds: Array<GeckoVerticalWeld> = model.verticalWeldsArray
    const horizontalWelds: Array<GeckoHorizontalWeld> =
      model.horizontalWeldsArray
    const plates: Array<GeckoPlate> = model.platesArray
    const polygonalPlates: Array<GeckoPolygonalPlate> =
      model instanceof GeckoVerticalTankShell ? model.polygonalPlatesArray : []
    const courses: Array<GeckoShellCourse> =
      model instanceof GeckoVerticalTankShell ? model.coursesArray : []

    const coatingRegions: Array<GeckoCoatingRegion> =
      model instanceof GeckoVerticalTankShell ? model.coatingRegionsArray : []

    const linerRegions: Array<GeckoLinerRegion> =
      model instanceof GeckoVerticalTankShell ? model.linerRegionsArray : []

    const scale = extractScale(groupProps.scale).multiply(
      show3D
        ? model instanceof GeckoHorizontalTankShell
          ? new Vector3(
              model.componentBounds.width.num,
              model.radius.num,
              model.radius.num
            )
          : new Vector3(
              model.radius.num,
              model.componentBounds.height.num,
              model.radius.num
            )
        : new Vector3(
            model.componentBounds.width.num,
            model.componentBounds.height.num,
            1.0
          )
    )
    return {
      bounds: model.componentBounds,
      scale: scale,
      manways: model.manwaysArray,
      rectangularManways: model.rectangularManwaysArray,
      nozzles: model.nozzlesArray,
      verticalWelds: verticalWelds,
      horizontalWelds: horizontalWelds,
      courses: courses,
      plates: plates,
      polygonalPlates: polygonalPlates,
      fixedSensors: model.fixedSensorsArray,
      coatingRegions,
      isHorizontalShell: model instanceof GeckoHorizontalTankShell,
      linerRegions,
    }
  }, [model, groupProps.scale, show3D])

  const geometry = React.useMemo(() => {
    if (!show3D) return planeGeometry
    if (isHorizontalShell) return yWrappedShellGeometry
    return xWrappedShellGeometry
  }, [show3D, isHorizontalShell])

  const getCompassPosition = React.useCallback<(angle: number) => Vec3>(
    (angle) => {
      const radians = (angle * Math.PI) / 180
      const fullCircle = 2 * Math.PI
      if (show3D) return [-Math.cos(radians), 1.0, Math.sin(radians)]
      const positionAlongWidth = model.componentBounds.width.scale(
        radians / fullCircle
      )
      const relativePositionAlongWidth = model.componentBounds.start.x
        .plus(positionAlongWidth)
        .scale(1.0 / model.componentBounds.width.num).num
      return [relativePositionAlongWidth, 1.0, 0]
    },
    [show3D, model]
  )

  const positionXTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const relX = coord
        .convertTo(bounds.x.unit)
        .minus(bounds.start.x)
        .scale(1.0 / bounds.width.num).num
      if (!show3D) return [relX, 0, 0]
      if (isHorizontalShell) return [relX, -1.1, 0]
      const angle = 2.0 * Math.PI * relX
      return [-Math.cos(angle), 0, Math.sin(angle)]
    },
    [show3D, bounds, isHorizontalShell]
  )

  const positionYTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const relY = coord
        .convertTo(bounds.y.unit)
        .minus(bounds.start.y)
        .scale(1.0 / bounds.height.num).num
      if (!show3D) return [0, relY, 0]
      if (!isHorizontalShell) return [-0.875, relY, 0.5]
      const angle = 2.0 * Math.PI * relY
      return [0, -Math.cos(angle), Math.sin(angle)]
    },
    [show3D, bounds, isHorizontalShell]
  )

  return (
    <ModelSurface3D
      geometry={geometry}
      bounds={bounds}
      componentModel={model}
      {...groupProps}
      // Apply the true scaling last (override anything in groupProps)
      scale={scale}>
      <TankDecorGroup3D yPivot={show3D && !isHorizontalShell}>
        <Ruler3D
          anchor={'right'}
          start={model.componentBounds.start.y}
          end={model.componentBounds.end.y}
          includeTicks
          includeStart
          includeEnd
          getTickPosition={positionYTick}
        />
      </TankDecorGroup3D>
      <TankDecorGroup3D xPivot={show3D && isHorizontalShell}>
        <Ruler3D
          anchor={'top'}
          start={model.componentBounds.start.x}
          end={model.componentBounds.end.x}
          includeTicks
          includeStart
          includeEnd={!show3D}
          getTickPosition={positionXTick}
        />
      </TankDecorGroup3D>
      {isHorizontalShell ? null : (
        <>
          <Compass3D
            componentModel={model}
            angleDeltaDegrees={30}
            assetModel={assetModel}
            getRotationPosition={getCompassPosition}
          />
          <CompassLines
            angleDeltaDegrees={30}
            assetModel={assetModel}
            getRotationPosition={getCompassPosition}
          />
        </>
      )}
      {model instanceof GeckoVerticalTankShell
        ? courses.map((course) => (
            <ShellCourseFeature3D
              key={course.id}
              component={model}
              model={course}
            />
          ))
        : null}
      <FeatureDispatch
        features={[
          manways,
          rectangularManways,
          nozzles,
          verticalWelds,
          horizontalWelds,
          coatingRegions,
          linerRegions,
          plates,
          fixedSensors,
          polygonalPlates,
        ]}
      />
    </ModelSurface3D>
  )
}
